import { useEffect, useState } from "react";
import { ApiError, api } from "../api/client";

/**
 * Polls {@code GET /api/system/state}. The backend's
 * {@code StartupReconciler.currentState()} is the authoritative
 * source (SPEC §19.18, §19.19).
 *
 * <h2>Partial-un-stubbing discipline (B5b)</h2>
 * The hook distinguishes four states:
 * <ul>
 *   <li>{@code null} — initial in-flight load; banner suppressed</li>
 *   <li>{@code state: SystemState} — successful response</li>
 *   <li>{@code connecting: true} — the ingress has no ready upstream
 *       (HTTP 502/503/504): the backend pod is still starting or
 *       briefly rolling. This is the expected boot transient and must
 *       NOT show the hard ERROR banner — that conflation was the B7.0
 *       "startup-race 503 noise" that masked the real system state.</li>
 *   <li>{@code error: true} — a genuine failure (5xx other than the
 *       gateway class, 4xx, or a network error). The StateBanner shows
 *       an explicit ERROR banner so the dashboard never silently falls
 *       back to "HEALTHY" the way the S37 stub did.</li>
 * </ul>
 *
 * <p>Both {@code connecting} and {@code error} keep {@code state = null}:
 * the "never fall back to a stale HEALTHY" rule holds in either case.
 */

export type SystemState = "HEALTHY" | "SAFE_SUSPENDED" | "DEGRADED" | "SCALING_REVIEW";

export interface SystemStateResult {
  state: SystemState | null;
  /** True iff the last poll failed with a genuine (non-transient) error. */
  error: boolean;
  /**
   * True iff the last poll failed because the ingress had no ready
   * upstream (HTTP 502/503/504) — the pod is starting or rolling.
   * Optional so existing {@code SystemStateResult} call sites/tests
   * that predate B7.0 still type-check; the hook always sets it.
   */
  connecting?: boolean;
  /**
   * F3: whether the manual emergency stop (kill-switch) is engaged, from the
   * {@code manualEmergencyStop} field of {@code GET /api/system/state}.
   * {@code null} while unknown (in-flight / failed poll).
   */
  emergencyStop?: boolean | null;
  /** Force an immediate re-poll (e.g. right after engaging the kill-switch). */
  refresh?: () => void;
}

/** HTTP statuses the nginx ingress returns when no backend pod is Ready. */
function isUpstreamUnready(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 502 || e.status === 503 || e.status === 504);
}

export function useSystemState(intervalMs = 5_000): SystemStateResult {
  const [state, setState] = useState<SystemState | null>(null);
  const [error, setError] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [emergencyStop, setEmergencyStop] = useState<boolean | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.systemState();
        if (cancelled) return;
        setState(res.state);
        setEmergencyStop(res.manualEmergencyStop ?? false);
        setError(false);
        setConnecting(false);
      } catch (e) {
        if (cancelled) return;
        // Drop the previous value to avoid the partial-un-stubbing
        // failure mode: never let stale "HEALTHY" persist while the
        // endpoint is failing.
        setState(null);
        setEmergencyStop(null);
        if (isUpstreamUnready(e)) {
          // Boot/rollout transient — render "connecting", not ERROR.
          setConnecting(true);
          setError(false);
        } else {
          setError(true);
          setConnecting(false);
        }
      }
    };
    void tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [intervalMs, reload]);

  return { state, error, connecting, emergencyStop, refresh: () => setReload((x) => x + 1) };
}
